/**
 * BUTTONS.TSX - Centralized Button Component System
 * 
 * WHY THIS FILE EXISTS:
 * ====================
 * This file centralizes ALL button logic and styling across the entire application.
 * Instead of having custom button implementations scattered throughout components,
 * we maintain a single source of truth for button behavior, styling, and consistency.
 * 
 * KEY BENEFITS:
 * - Consistent sizing across all buttons (37px height for toolbar buttons)
 * - Dynamic style switching (Classic/Original/Modern) that affects ALL buttons globally
 * - Centralized maintenance - change button behavior once, affects everywhere
 * - Prevents styling conflicts and inconsistencies
 * - Follows React best practices for reusable components
 * 
 * HOW IT WORKS:
 * ============
 * 1. DYNAMIC STYLE SYSTEM: Buttons can switch between "Classic", "Original", and "Modern" styles
 *    based on global settings (used in Organization Management)
 * 
 * 2. SIZE SYSTEM: All buttons respect size props (xs, sm, md, lg) for consistent dimensions
 *    - sm = 37px height (used for all toolbar buttons)
 *    - md = default size
 * 
 * 3. VARIANT SYSTEM: Different button types (primary, secondary, filter, toggle, etc.)
 *    each have their own styling but respect the size and style systems
 * 
 * 4. CACHE SYSTEM: Style configurations are memoized for performance
 * 
 * IMPORTANT NOTES:
 * ===============
 * - NEVER add hardcoded padding (px-3 py-2) to variants - always use size props
 * - All toolbar buttons MUST use size="sm" for 37px height consistency
 * - When adding new variants, ensure they work with all three style systems
 * - The toggle variant was problematic due to border conflicts, so it has special handling
 */

import { forwardRef, useMemo, useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { getButtonStyle } from "@/lib/globalStore";

// BUTTON VARIANTS: Different visual styles for different use cases
// ==============================================================
// ✅ ADDING NEW VARIANTS IS ENCOURAGED! 
// When you need a new button style, add it here instead of creating custom buttons elsewhere.
// This ensures consistency and allows the new variant to benefit from the dynamic style system.
type ButtonVariant = 
  | "primary"      // Main action (cyan background) - "Add" buttons
  | "secondary"    // Secondary action (muted background) - "Export" buttons
  | "ghost"        // Minimal action (no background, hover only)
  | "danger"       // Destructive action (subtle red) - Delete/Logout buttons
  | "success"      // Success action (green) - Confirm actions
  | "warning"      // Warning action (amber) - Caution actions
  | "toggle"       // Toggle button (for view toggles) - List/Grid/Map buttons
  | "icon"         // Icon-only button - Small action buttons
  | "link"         // Link-style button (underline on hover)
  | "textlink"     // Simple text with underline (for Client/Scooter links)
  | "filter"       // Filter button style - "Filter" buttons in toolbars
  | "retry";       // Retry button style
  // Add new variants here as needed, but implement them in all three style systems below

// BUTTON SIZES: Controls height and padding
// sm = 37px height (REQUIRED for all toolbar buttons for consistency)
type ButtonSize = "xs" | "sm" | "md" | "lg" | "icon";

// BUTTON STYLES: Global style themes that can be switched dynamically
// Used in Organization Management to change the look of ALL buttons
type ButtonStyle = "modern" | "classic" | "original";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  buttonStyle?: ButtonStyle;
  children: React.ReactNode;
  active?: boolean; // For toggle buttons
}

// PERFORMANCE OPTIMIZATION: Style caching system
// =============================================
// Style configurations are expensive to compute, so we cache them and only
// recalculate when the button style changes (Classic/Original/Modern switch)
const STYLE_CACHE = new Map<string, { baseClasses: string; variants: Record<string, string> }>();

// Cache invalidation mechanism for dynamic style changes
// Called when user changes button style in Organization Management
const invalidateStyleCache = () => {
  STYLE_CACHE.clear();
};

// Listen for button style changes to invalidate cache
// This allows the entire app to switch button styles dynamically
if (typeof window !== 'undefined') {
  window.addEventListener('buttonStyleChanged', invalidateStyleCache);
}

// STYLE CONFIGURATION SYSTEM
// ==========================
// This function defines the CSS classes for each button style theme.
// Each style (Classic/Original/Modern) has its own set of base classes and variants.
// This allows the entire app to switch button appearances globally.
const getStyleConfig = (buttonStyle: ButtonStyle) => {
  const cacheKey = buttonStyle;
  if (STYLE_CACHE.has(cacheKey)) {
    return STYLE_CACHE.get(cacheKey)!;
  }

  let baseClasses: string;
  let variants: Record<string, string>;


  if (buttonStyle === "classic") {
    baseClasses = "inline-flex items-center justify-center font-medium transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed border";
    variants = {
      // ⚠️  IMPORTANT: When adding new variants, you MUST add them to ALL THREE style systems:
      // 1. Classic (this section)
      // 2. Original (below)  
      // 3. Modern (at the bottom)
      // NEVER add hardcoded padding (px-3 py-2) - let the size system handle dimensions
      primary: "bg-accent-cyan text-white border-accent-cyan hover:bg-accent-cyan/90 hover:border-accent-cyan/90 focus:ring-accent-cyan/50 rounded-md shadow-sm hover:shadow-md",
      secondary: "bg-bg-e2 text-text-muted border-border-subtle hover:bg-bg-elevated hover:text-text hover:border-border-subtle/80 focus:ring-border-subtle rounded-md shadow-sm hover:shadow-md",
      ghost: "bg-transparent text-text-muted border-transparent hover:bg-bg-e2 hover:text-text focus:ring-border-subtle rounded-md",
      danger: "bg-red-500/8 text-red-400/80 border-red-500/20 hover:bg-red-500/12 hover:border-red-500/30 hover:text-red-400 focus:ring-red-500/30 rounded-md shadow-sm hover:shadow-md",
      success: "bg-green-500/90 text-white border-green-500/90 hover:bg-green-500 hover:border-green-500 focus:ring-green-500/50 rounded-md shadow-sm hover:shadow-md",
      warning: "bg-amber-500/90 text-white border-amber-500/90 hover:bg-amber-500 hover:border-amber-500 focus:ring-amber-500/50 rounded-md shadow-sm hover:shadow-md",
      toggle: "rounded-md border transition-all duration-200 focus:ring-border-subtle",
      icon: "p-2 bg-transparent text-text-muted border-transparent hover:bg-bg-e2 hover:text-text focus:ring-border-subtle rounded-md",
      link: "text-accent-cyan hover:text-accent-cyan/80 hover:underline focus:ring-accent-cyan/50 p-0 bg-transparent border-0 font-medium transition-all duration-200",
      textlink: "inline text-text underline underline-offset-2 decoration-dotted hover:text-text/80 hover:no-underline focus:ring-border-subtle p-0 m-0 bg-transparent border-0 font-normal transition-all duration-200 text-right h-auto",
      filter: "rounded-md border border-border-subtle bg-bg-e2 hover:bg-bg-elevated focus:ring-border-subtle shadow-sm hover:shadow-md transition-all duration-200",
      retry: "text-accent-cyan hover:text-accent-cyan/80 hover:underline focus:ring-accent-cyan/50 p-0 bg-transparent border-0 font-medium transition-all duration-200",
    };
  } else if (buttonStyle === "original") {
    baseClasses = "inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
    variants = {
      primary: "bg-accent-cyan/15 text-accent-cyan border border-accent-cyan/30 hover:bg-accent-cyan/30 hover:text-accent-cyan hover:border-accent-cyan/60 hover:shadow-sm focus:ring-accent-cyan/50 rounded-lg",
      secondary: "bg-bg-e2 text-text-muted border border-border-subtle hover:bg-bg-elevated focus:ring-border-subtle rounded-lg",
      ghost: "text-text-muted hover:bg-bg-e2 focus:ring-border-subtle rounded-lg",
      danger: "bg-red-500/6 text-red-400/70 border border-red-500/15 hover:bg-red-500/10 hover:border-red-500/25 hover:text-red-400/90 focus:ring-red-500/30 rounded-lg",
      success: "bg-green-500/15 text-green-400 border border-green-500/30 hover:bg-green-500/25 focus:ring-green-500/50 rounded-lg",
      warning: "bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25 focus:ring-amber-500/50 rounded-lg",
      toggle: "focus:ring-border-subtle",
      icon: "p-2 text-text-muted hover:bg-bg-e2 focus:ring-border-subtle rounded-lg",
      link: "text-accent-cyan hover:text-accent-cyan/80 hover:underline focus:ring-accent-cyan/50 p-0 bg-transparent border-0",
      textlink: "inline text-text underline underline-offset-2 decoration-dotted hover:text-text/80 hover:no-underline focus:ring-border-subtle p-0 m-0 bg-transparent border-0 font-normal transition-all duration-200 text-right h-auto",
      filter: "rounded-lg border border-border-subtle bg-bg-e2 hover:bg-bg focus:ring-border-subtle",
      retry: "text-accent-cyan underline hover:text-accent-cyan/80 hover:no-underline focus:ring-accent-cyan/50 p-0 bg-transparent border-0",
    };
  } else {
    // Modern (default)
    baseClasses = "inline-flex items-center justify-center font-semibold transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl";
    variants = {
      primary: "bg-gradient-to-br from-accent-cyan via-accent-cyan/80 to-accent-cyan/60 text-white border-0 hover:from-accent-cyan/90 hover:via-accent-cyan/70 hover:to-accent-cyan/50 focus:ring-accent-cyan/50 rounded-2xl backdrop-blur-sm shadow-lg hover:shadow-xl",
      secondary: "bg-gradient-to-br from-bg-elevated via-bg-e2 to-bg-elevated text-text border-0 hover:from-bg-elevated hover:via-bg-elevated hover:to-bg-e2 focus:ring-border-subtle rounded-2xl shadow-md hover:shadow-lg backdrop-blur-sm",
      ghost: "text-text-muted hover:text-text hover:bg-gradient-to-br hover:from-bg-e2/50 hover:to-bg-elevated/30 focus:ring-border-subtle rounded-2xl backdrop-blur-sm shadow-sm hover:shadow-md",
      danger: "bg-gradient-to-r from-red-500/8 to-red-500/4 text-red-400/80 border border-red-500/20 hover:from-red-500/12 hover:to-red-500/8 hover:border-red-500/30 hover:text-red-400 focus:ring-red-500/30 rounded-xl backdrop-blur-sm",
      success: "bg-gradient-to-r from-green-500/20 to-green-500/10 text-green-400 border border-green-500/40 hover:from-green-500/30 hover:to-green-500/20 hover:border-green-500/60 focus:ring-green-500/50 rounded-xl backdrop-blur-sm",
      warning: "bg-gradient-to-r from-amber-500/20 to-amber-500/10 text-amber-400 border border-amber-500/40 hover:from-amber-500/30 hover:to-amber-500/20 hover:border-amber-500/60 focus:ring-amber-500/50 rounded-xl backdrop-blur-sm",
      toggle: "rounded-2xl border-0 transition-all duration-200 focus:ring-border-subtle shadow-md hover:shadow-lg backdrop-blur-sm",
      icon: "p-2 text-text-muted hover:text-text hover:bg-bg-e2/80 focus:ring-border-subtle rounded-xl backdrop-blur-sm shadow-sm hover:shadow-md",
      link: "text-text-muted hover:text-text hover:underline focus:ring-border-subtle p-0 bg-transparent border-0 font-medium transition-all duration-200",
      textlink: "inline text-text underline underline-offset-2 decoration-dotted hover:text-text/80 hover:no-underline focus:ring-border-subtle p-0 m-0 bg-transparent border-0 font-normal transition-all duration-200 text-right h-auto",
      filter: "rounded-xl border border-border-subtle bg-gradient-to-r from-bg-e2 to-bg-elevated hover:from-bg-elevated hover:to-bg-e2 focus:ring-border-subtle shadow-sm hover:shadow-md transition-all duration-200",
      retry: "underline text-text-muted hover:text-text focus:ring-border-subtle p-0 bg-transparent border-0 font-medium transition-all duration-200 hover:no-underline",
    };
  }

  const config = { baseClasses, variants };
  STYLE_CACHE.set(cacheKey, config);
  return config;
};

// Memoized sizes to prevent object recreation - updated for consistent toolbar heights
// SIZE SYSTEM: Controls button dimensions and text size
// ====================================================
// CRITICAL: All toolbar buttons MUST use size="sm" for 37px height consistency
// This ensures all toolbar elements (search, buttons, toggles) have the same height
const SIZES = {
  xs: "px-3 py-1.5 text-xs font-medium min-h-[44px] sm:min-h-0",    // Small buttons - mobile touch target
  sm: "px-4 py-2 text-sm font-medium min-h-[44px] sm:min-h-0",      // TOOLBAR SIZE: 37px height - mobile touch target
  md: "px-5 py-2.5 text-base font-medium min-h-[44px] sm:min-h-0",  // Default size - mobile touch target
  lg: "px-6 py-3 text-lg font-medium min-h-[44px] sm:min-h-0",      // Large buttons - mobile touch target
  icon: "p-2.5 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0",    // Icon-only buttons - mobile touch target
} as const;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "secondary", size = "md", buttonStyle = "original", active = false, children, ...props }, ref) => {
    // Get button style from global settings or default to original
    const currentButtonStyle = getButtonStyle() || buttonStyle;
    
    
    // Force re-render when global button style changes by using a state that updates on events
    const [styleVersion, setStyleVersion] = useState(0);
    
    useEffect(() => {
      const handleStyleChange = () => {
        setStyleVersion(prev => prev + 1);
      };
      
      window.addEventListener('buttonStyleChanged', handleStyleChange);
      return () => window.removeEventListener('buttonStyleChanged', handleStyleChange);
    }, []);
    
    // Memoize style configuration to prevent recalculation
    const styleConfig = useMemo(() => getStyleConfig(currentButtonStyle), [currentButtonStyle, styleVersion]);
    
    // TOGGLE VARIANT SPECIAL HANDLING
    // ===============================
    // Toggle buttons (List/Grid/Map view selectors) need special handling because:
    // 1. They have active/inactive states that need different styling
    // 2. They were causing border conflicts when using the standard variant system
    // 3. They need to work seamlessly in grouped button containers
    const toggleVariant = useMemo(() => {
      if (variant !== "toggle") return styleConfig.variants[variant];
      
      if (currentButtonStyle === "classic") {
        return `rounded-md transition-all duration-200 ${active ? "bg-accent-cyan text-white shadow-md" : "bg-bg-e2 text-text-muted border border-border-subtle hover:bg-bg-elevated hover:text-text"} focus:ring-border-subtle`;
      } else if (currentButtonStyle === "original") {
        return `${active ? "bg-bg-e2" : "hover:bg-bg-e2"} focus:ring-border-subtle`;
      } else {
        return `rounded-2xl transition-all duration-200 ${active ? "bg-gradient-to-br from-accent-cyan via-accent-cyan/80 to-accent-cyan/60 text-white shadow-lg hover:shadow-xl backdrop-blur-sm" : "bg-gradient-to-br from-bg-elevated via-bg-e2 to-bg-elevated text-text-muted hover:from-bg-elevated hover:via-bg-elevated hover:to-bg-e2 hover:text-text shadow-md hover:shadow-lg backdrop-blur-sm"} focus:ring-border-subtle`;
      }
    }, [variant, active, currentButtonStyle, styleConfig.variants, styleVersion]);

    // Memoize final classes
    const finalClasses = useMemo(() => {
      const variantClass = variant === "toggle" ? toggleVariant : styleConfig.variants[variant];
      
      // Special handling for textlink variant to override base classes
      if (variant === "textlink") {
        return cn(
          "inline text-text underline underline-offset-2 decoration-dotted hover:text-text/80 hover:no-underline focus:ring-border-subtle p-0 m-0 bg-transparent border-0 font-normal transition-all duration-200 text-right h-auto",
          className
        );
      }
      
      return cn(
        styleConfig.baseClasses,
        variantClass,
        SIZES[size],
        className
      );
    }, [styleConfig.baseClasses, variant, toggleVariant, size, className, styleVersion]);

    return (
      <button
        className={finalClasses}
        style={currentButtonStyle === "original" ? {
          fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"'
        } : undefined}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";

export default Button;
export type { ButtonProps, ButtonVariant, ButtonSize, ButtonStyle };

/**
 * LESSONS LEARNED & CRITICAL RULES:
 * ================================
 * 
 * 1. NEVER ADD HARDCODED PADDING TO VARIANTS
 *    - Don't use px-3 py-2 in variant definitions
 *    - Always let the size system handle dimensions
 *    - This was the root cause of inconsistent button heights
 * 
 * 2. ALL TOOLBAR BUTTONS MUST USE size="sm"
 *    - This ensures 37px height consistency across all toolbar elements
 *    - Applies to: Add, Filter, Export, View Toggle buttons
 * 
 * 3. CENTRALIZE ALL BUTTON LOGIC HERE
 *    - Don't create custom button implementations in other components
 *    - Use this component for ALL buttons to maintain consistency
 *    - This allows global style changes to affect the entire app
 *    - ✅ ADDING NEW VARIANTS IS ENCOURAGED - just add them here instead of elsewhere
 * 
 * 4. THE TOGGLE VARIANT IS SPECIAL
 *    - It has complex active/inactive state logic
 *    - It was causing border conflicts that required special handling
 *    - Don't modify its logic without understanding the implications
 * 
 * 5. DYNAMIC STYLE SYSTEM IS POWERFUL
 *    - Users can switch between Classic/Original/Modern styles globally
 *    - This affects ALL buttons in the entire application
 *    - The caching system makes this performant
 */